import {inject, injectable} from 'inversify';

import type {MaybeExpanded} from '../../../../../api/types';
import type {BNFormApiModel} from '../../../../../api/types/bnrks/models';
import type {CoreFormApiModel} from '../../../../../api/types/core/models';
import type {Combine} from '../../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../../ports/types';

import {FileMapperJrpc} from '../../../File/mappers/File.mapper.jrpc';
import {QuestionMapperJrpc} from '../../../Question/mappers/jrpc/Question.mapper.jrpc';
import {AssessmentFactory} from '../../Assessment.factory';
import {AssessmentModel} from '../../Assessment.model';
import {AssessmentExpandJrpc} from './Assessment.expand.jrpc';

@injectable()
export class AssessmentMapperJrpc
  implements
    IApiDataMapper<
      AssessmentMapperJrpc.ExpandedApiData,
      AssessmentModel.Props,
      AssessmentModel
    >
{
  constructor(
    @inject(AssessmentFactory)
    private readonly assessmentFactory: AssessmentFactory,
    @inject(FileMapperJrpc)
    private readonly fileMapper: FileMapperJrpc,
    @inject(QuestionMapperJrpc)
    private readonly questionMapper: QuestionMapperJrpc,
    @inject(AssessmentExpandJrpc)
    readonly expand: AssessmentExpandJrpc,
  ) {}

  mapDataToModel(data: AssessmentMapperJrpc.ExpandedApiData) {
    return this.assessmentFactory.create(this.mapDataToProps(data));
  }

  mapDataToProps(
    data: AssessmentMapperJrpc.ExpandedApiData,
  ): AssessmentModel.Props {
    return {
      avatar: data.avatar ? this.fileMapper.mapDataToModel(data.avatar) : null,
      description: data.description,
      diagram: this.diagram(data),
      id: data._id,
      questions: this.questions(data),
      showDescriptionToClient: data.show_description_to_client,
      status: this.status(data),
      timeToFill: data.time_to_fill,
      title: data.title,
    };
  }

  mapToModelOrEmpty(data: MaybeExpanded<AssessmentMapperJrpc.ExpandedApiData>) {
    if (!data) {
      return this.assessmentFactory.createEmpty();
    }

    return this.mapDataToModel(data);
  }

  private diagram(data: AssessmentMapperJrpc.ExpandedApiData) {
    if (!data.diagram) {
      return {points: [], style: AssessmentModel.DiagramStyle.BLUE};
    }

    const styles: Record<
      NonNullable<AssessmentMapperJrpc.ExpandedApiData['diagram']>['style'],
      AssessmentModel.DiagramStyle
    > = {
      blue: AssessmentModel.DiagramStyle.BLUE,
      ['green-red']: AssessmentModel.DiagramStyle.GREEN_RED,
      ['red-green']: AssessmentModel.DiagramStyle.RED_GREEN,
    };

    return {
      points: data.diagram.points,
      style: styles[data.diagram.style] ?? AssessmentModel.DiagramStyle.BLUE,
    };
  }

  private questions(
    data: AssessmentMapperJrpc.ExpandedApiData,
  ): AssessmentModel.Props['questions'] {
    return this.questionMapper.mapSectionsToModels(data.sections);
  }

  private status(
    data: AssessmentMapperJrpc.ExpandedApiData,
  ): AssessmentModel.Status {
    const statuses: Record<
      AssessmentMapperJrpc.ExpandedApiData['status'],
      AssessmentModel.Status
    > = {
      active: AssessmentModel.Status.ACTIVE,
      archived: AssessmentModel.Status.ARCHIVED,
      deleted: AssessmentModel.Status.DELETED,
      hidden: AssessmentModel.Status.HIDDEN,
    };

    return statuses[data.status] ?? AssessmentModel.Status.ACTIVE;
  }
}

export namespace AssessmentMapperJrpc {
  export type ApiData = Combine<CoreFormApiModel, BNFormApiModel>;

  export type ExpandedApiData = ApiData & AssessmentExpandJrpc.ExpandedFields;
}
